import { Check, X, Phone, Star, ShieldCheck, HeartHandshake, ArrowRight } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const Frame = ({ url, id, children }) => (
  <div className="overflow-hidden rounded-[3px] border border-bone/10 bg-[#1c1c1e]">
    <div className="flex items-center gap-3 border-b border-bone/10 bg-[#111113] px-4 py-2.5">
      <div className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-bone/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-bone/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-bone/20" />
      </div>
      <span data-testid={`vergleich-url-${id}`} className="flex-1 truncate rounded-full bg-bone/5 px-3 py-1 font-mono text-[10px] text-bone/50">{url}</span>
    </div>
    {children}
  </div>
);

const Badge = ({ children, tone }) => (
  <span className={`mb-4 inline-block self-start rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] ${tone === "bad" ? "border-red-400/40 text-red-300" : "border-gold/40 text-gold"}`}>
    {children}
  </span>
);

const VorherNachher = () => {
  const { t } = useLanguage();
  const v = t.vergleich;
  return (
    <section id="vergleich" data-testid="vergleich-section" className="bg-dark py-20 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-16">
        <Reveal><Eyebrow>{v.eyebrow}</Eyebrow></Reveal>
        <div className="mt-4 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <Reveal delay={0.08}>
            <h2 className="max-w-3xl font-display text-4xl font-medium tracking-tight sm:text-5xl">{v.title}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="max-w-md text-base leading-relaxed text-inkmuted">{v.sub}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12">
          <Reveal delay={0.1}>
            <div data-testid="vergleich-schlecht-card" className="flex h-full flex-col">
              <Badge tone="bad">{v.bad.badge}</Badge>
              <Frame url={v.bad.url} id="schlecht">
                <div style={{ fontFamily: "'Comic Sans MS','Comic Neue','Brush Script MT',cursive" }} className="bg-[#e9f5d3]">
                  <div className="flex items-center justify-between bg-[#000080] px-4 py-2">
                    <span className="text-xs font-bold text-[#ffe14d]">{v.bad.name}</span>
                    <span className="hidden gap-3 text-[9px] text-white underline sm:flex">
                      {v.bad.nav.map((n) => <span key={n}>{n}</span>)}
                    </span>
                  </div>
                  <div className="px-5 py-6 text-center">
                    <p className="text-base font-bold leading-snug text-[#cc0000] sm:text-lg">{v.bad.headline}</p>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="animate-pulse rounded-sm bg-[#ff0000] px-1.5 py-0.5 text-[9px] font-bold text-white">{v.bad.neu}</span>
                      <span className="text-[9px] text-[#006600] underline">{v.bad.welcome}</span>
                    </div>
                    <div className="mx-auto mt-3 h-[3px] max-w-[220px] bg-gradient-to-r from-[#ff0000] via-[#ffe14d] to-[#00aa00]" aria-hidden="true" />
                    <div className="mt-3 flex flex-wrap justify-center gap-2">
                      {v.bad.nav.map((n) => (
                        <span key={n} style={{ border: "2px outset #ccc", background: "#d4d0c8", color: "#000", fontSize: 9, padding: "2px 10px" }}>{n}</span>
                      ))}
                    </div>
                    <p className="mt-4 text-justify text-[9px] leading-tight text-[#333]">{v.bad.body}</p>
                    <div className="mt-3 flex justify-center">
                      <span className="bg-black px-2 py-0.5 font-mono text-[9px] text-[#39ff14]">{v.bad.counter}</span>
                    </div>
                    <p className="mt-2 text-[8px] italic text-[#888]">{v.bad.ie}</p>
                  </div>
                </div>
              </Frame>
              <ul className="mt-6 space-y-2.5">
                {v.bad.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-bone/75">
                    <X size={15} className="mt-0.5 shrink-0 text-red-400" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div data-testid="vergleich-gut-card" className="flex h-full flex-col">
              <Badge tone="good">{v.good.badge}</Badge>
              <Frame url={v.good.url} id="gut">
                <div className="bg-white">
                  <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-[3px] bg-amber-400 font-display text-[11px] font-bold text-neutral-900">M</span>
                      <span className="font-display text-sm font-semibold tracking-tight text-neutral-900">{v.good.name}</span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-900 px-3 py-1.5 text-[10px] font-medium text-white">
                      <Phone size={10} /> {v.good.phone}
                    </span>
                  </div>
                  <div className="relative h-48 overflow-hidden sm:h-56">
                    <img src="/baustein-personal.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 26%" }} />
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/10" aria-hidden="true" />
                    <div className="relative flex h-full flex-col justify-center gap-3 px-6 sm:px-8">
                      <span className="w-fit rounded-full bg-amber-400/20 px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.2em] text-amber-700">{v.good.tag}</span>
                      <p className="max-w-[250px] font-display text-lg font-medium leading-snug tracking-tight text-neutral-900 sm:text-2xl">{v.good.headline}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-[3px] bg-amber-400 px-3.5 py-1.5 text-[10px] font-semibold text-neutral-900">{v.good.cta}</span>
                        <span className="rounded-[3px] border border-neutral-300 bg-white/70 px-3.5 py-1.5 text-[10px] text-neutral-700">{v.good.cta2}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-6 py-3 sm:px-8">
                    <span className="inline-flex items-center gap-1.5 text-[10px] text-neutral-500">
                      <span className="inline-flex gap-0.5" aria-hidden="true">
                        {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={10} className="fill-amber-400 text-amber-400" />)}
                      </span>
                      {v.good.reviews}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[10px] text-neutral-500">
                      <ShieldCheck size={11} className="text-emerald-600" /> {v.good.chips[2]}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-6 py-4 sm:px-8">
                    {v.good.chips.slice(0, 2).map((c) => (
                      <div key={c} className="rounded-[3px] bg-neutral-50 p-2.5">
                        <HeartHandshake size={13} className="text-amber-600" />
                        <p className="mt-1.5 text-[9px] leading-tight text-neutral-600">{c}</p>
                      </div>
                    ))}
                    <div className="rounded-[3px] bg-neutral-900 p-2.5">
                      <Phone size={13} className="text-amber-400" />
                      <p className="mt-1.5 text-[9px] leading-tight text-white/80">{v.good.phone}</p>
                    </div>
                  </div>
                </div>
              </Frame>
              <ul className="mt-6 space-y-2.5">
                {v.good.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-bone/75">
                    <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <p data-testid="vergleich-note" className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-inkmuted">{v.note}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col items-start gap-6 border-t border-bone/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-display text-2xl font-medium tracking-tight text-bone">{v.cta}</p>
            <button
              type="button"
              data-testid="vergleich-cta-btn"
              onClick={() => scrollToId("kontakt")}
              className="group inline-flex items-center gap-2 rounded-[3px] bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
            >
              {v.ctaBtn}
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default VorherNachher;
